import { useCallback, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { INSURANCE, getFact } from './mockData';
import type { FactPopoverData, InsurancePatient, InsurancePlan } from './types';
import { daysUntil, fmtDateTime, fmtStamp, money } from './utils';
import { InsuranceProvider, type OpenFactArgs } from './InsuranceContext';
import { CallDrawer, FactPopover, PortalDrawer } from './overlays';
import { NeedsAttention, Opportunities, VisitEstimate } from './TopSections';
import { BeforeClaimGoesOut, Claims, FrequencyTable, PlanCoverage, PlanRules, PlannedTreatment, PreAuths } from './CoverageSections';
import { VerificationSection } from './VerificationSection';
import { PrintReport } from './PrintReport';
import './insurance.css';

const fx = INSURANCE;

type TabKey = 'cov' | 'rules' | 'tx' | 'src';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'cov', label: 'Coverage' },
  { key: 'rules', label: 'Plan rules' },
  { key: 'tx', label: 'Treatment & claims' },
  { key: 'src', label: 'How ELVA verified' },
];

/** Patient identity card (visual pass §6): avatar, name, age + language,
 *  contact, a verification success pill, and the next-visit strip. */
function PatientHeader({ patient: p, onSources }: { patient: InsurancePatient; onSources: () => void }) {
  const v = fx.verification;
  return (
    <section className="ins-card" aria-labelledby="ins-pname" style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="ins-phead">
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', minWidth: 0 }}>
          <div aria-hidden className="ins-avatar">{p.initials}</div>
          <div style={{ minWidth: 0 }}>
            <h1 id="ins-pname" className="ins-pname">{p.name}</h1>
            <div className="ins-pline">{p.age} · patient since {p.since} · speaks {p.language}</div>
            <div className="ins-pline">{p.phone} · {p.email} · {p.contactPreference}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span className="ins-verifpill"><span className="ins-dot" style={{ margin: 0, background: 'var(--color-success)' }} />Coverage verified</span>
          <span className="ins-note" style={{ marginTop: 0, textAlign: 'right' }}>
            {fmtStamp(v.lastAt)} · <button type="button" className="ins-src" onClick={onSources}>{v.sources} sources</button>
          </span>
        </div>
      </div>
      <div className="ins-nextvisit">
        <span className="ins-label">Next visit</span>
        {p.nextVisit.at ? (
          <>
            <strong style={{ fontSize: 15 }}>{fmtDateTime(p.nextVisit.at)}</strong>
            <span style={{ fontSize: 14, color: 'var(--color-foreground-secondary)' }}>
              {p.nextVisit.type}{p.nextVisit.provider ? ` · ${p.nextVisit.provider}` : ''}
            </span>
          </>
        ) : (
          <strong style={{ fontSize: 15 }}>None booked</strong>
        )}
      </div>
    </section>
  );
}

/** One plan's rail card — identity, money left with a bar, the allowances-left
 *  summary, IDs, verification and Re-verify (visual pass §2 "the rail"). */
function RailPlanCard({ plan }: { plan: InsurancePlan }) {
  const [reverifying, setReverifying] = useState(false);
  const pct = Math.min(100, Math.round((plan.used / plan.annualMax) * 100));
  const days = daysUntil(plan.resets, fx.today);
  const order = plan.order === 'primary' ? 'Primary' : plan.order === 'secondary' ? 'Secondary' : 'Tertiary';
  const net = plan.network === 'in' ? 'in-network' : 'out-of-network';
  return (
    <section className="ins-card ins-railcard" aria-label={`${plan.carrier} plan`}>
      <div>
        <div className="ins-plan-eyebrow">{order} · {plan.type} · {net}</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{plan.carrier} <span style={{ fontWeight: 500, color: 'var(--color-muted-foreground)', fontSize: 15 }}>{plan.plan}</span></div>
      </div>
      <div>
        <div className="ins-railmax"><span className="v">{money(plan.remaining)}</span><span className="of">left of {money(plan.annualMax)}</span></div>
        <div className="ins-railbar" role="progressbar" aria-label={`${plan.carrier} annual maximum used`} aria-valuemin={0} aria-valuemax={plan.annualMax} aria-valuenow={plan.used}>
          <span style={{ width: `${String(pct)}%` }} />
        </div>
        <div className="ins-railends"><span>{money(plan.used)} used</span><span>Resets Dec 31{plan.order === 'primary' ? ` · ${String(days)} days` : ''}</span></div>
        {plan.maxWarning ? <div className="ins-railwarn" style={{ marginTop: 10 }}>{plan.maxWarning}</div> : null}
      </div>
      {plan.unconfirmed ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span className="ins-tag ins-tag-open">Not yet known</span>
          <span style={{ fontSize: 13.5, color: 'var(--color-foreground-secondary)' }}>{plan.unconfirmed.title}. {plan.unconfirmed.detail}</span>
        </div>
      ) : null}
      {plan.railSummary ? (
        <dl className="ins-railkv">
          {plan.railSummary.map((r) => (
            <div key={r.dt} style={{ display: 'contents' }}>
              <dt>{r.dt}</dt><dd>{r.dd}{r.note ? <span className="ins-note" style={{ marginTop: 0 }}> · {r.note}</span> : null}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
        <dl className="ins-railkv ids">
          <dt>Member</dt><dd>{plan.memberId}</dd>
          <dt>Group</dt><dd>{plan.group}</dd>
          <dt>Subscriber</dt><dd>{plan.subscriber} · {plan.relationship.toLowerCase()}</dd>
          <dt>Payer ID</dt><dd>{plan.payerId}</dd>
          {plan.order === 'primary' ? <><dt>Provider line</dt><dd>{plan.phone}</dd></> : null}
        </dl>
      </div>
      <div className="ins-railfoot">
        <span className="ins-note" style={{ marginTop: 0 }}>{plan.status === 'verified' ? `Verified ${fmtStamp(fx.verification.lastAt)} · ${String(plan.sourcesUsed ?? 5)} sources` : 'Partly verified'}</span>
        <button type="button" className="ins-btn ins-btn-secondary" disabled={reverifying} onClick={() => { setReverifying(true); window.setTimeout(() => { setReverifying(false); }, 1400); }}>
          {reverifying ? 'Re-verifying…' : 'Re-verify'}
        </button>
      </div>
    </section>
  );
}

/**
 * The patient insurance page. `/patients/:id` resolves the patient from the
 * directory and passes an identity override so the page renders THAT patient
 * (rcm-24 §6 — before this, every card opened the single Waylon fixture). The
 * deep clinical detail below the identity stays the shared demo fixture until
 * the backend lands (handoff §6/§7).
 */
export function InsurancePageView({
  patient,
  primaryPlan,
}: {
  patient?: InsurancePatient;
  primaryPlan?: InsurancePlan;
} = {}) {
  const pt = patient ?? fx.patient;
  const plans = primaryPlan
    ? fx.plans.map((pl) => (pl.order === 'primary' ? primaryPlan : pl))
    : fx.plans;

  const [fact, setFact] = useState<FactPopoverData | null>(null);
  const [call, setCall] = useState(false);
  const [portal, setPortal] = useState(false);
  const [wrong, setWrong] = useState(false);
  const [tab, setTab] = useState<TabKey>('cov');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const openFact = useCallback((a: OpenFactArgs) => { setFact(getFact(a.id, a.title, a.sources)); }, []);
  const openCall = useCallback(() => { setCall(true); }, []);
  const openPortal = useCallback(() => { setPortal(true); }, []);
  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const ctx = useMemo(() => ({ openFact, openCall, openPortal, scrollTo }), [openFact, openCall, openPortal, scrollTo]);

  const onTabKey = (e: KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = e.key === 'ArrowRight' ? (i + 1) % TABS.length : (i - 1 + TABS.length) % TABS.length;
    const t = TABS[next];
    if (t) { setTab(t.key); tabRefs.current[next]?.focus(); }
  };

  const onNeedsOpen = (target: string) => {
    if (target === 'tab:tx') { setTab('tx'); return; }
    if (target.startsWith('tab:')) { setTab(target.slice(4) as TabKey); return; }
    scrollTo(target);
  };

  return (
    <InsuranceProvider value={ctx}>
      <div className="rcm-ins-scroll">
        <div className="rcm-ins ins-main">
          <PatientHeader patient={pt} onSources={() => { setTab('src'); }} />

          <div className="ins-cols">
            <div className="ins-colmain">
              <VisitEstimate />
              <NeedsAttention onOpen={onNeedsOpen} />
              <Opportunities />

              <section className="ins-card" aria-label="Plan details" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="ins-tabs" role="tablist" aria-label="Plan details">
                  {TABS.map((t, i) => (
                    <button
                      key={t.key}
                      ref={(el) => { tabRefs.current[i] = el; }}
                      type="button"
                      role="tab"
                      id={`ins-tab-${t.key}`}
                      aria-selected={tab === t.key}
                      aria-controls={`ins-panel-${t.key}`}
                      tabIndex={tab === t.key ? 0 : -1}
                      className="ins-tab"
                      onClick={() => { setTab(t.key); }}
                      onKeyDown={(e) => { onTabKey(e, i); }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="ins-tabpanel" role="tabpanel" id={`ins-panel-${tab}`} aria-labelledby={`ins-tab-${tab}`} style={{ padding: '4px 20px 18px' }}>
                  {tab === 'cov' ? <PlanCoverage /> : null}
                  {tab === 'rules' ? <><PlanRules /><BeforeClaimGoesOut /></> : null}
                  {tab === 'tx' ? <><PlannedTreatment /><FrequencyTable /><Claims /><PreAuths /></> : null}
                  {tab === 'src' ? <VerificationSection /> : null}
                </div>
              </section>
            </div>

            <aside className="ins-rail" aria-label="Plans">
              {plans.map((p) => <RailPlanCard key={p.id} plan={p} />)}
              <section className="ins-card" aria-labelledby="ins-bill-h" style={{ padding: '16px 20px' }}>
                <div className="ins-label" id="ins-bill-h">Billing preference</div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>{fx.billingPreference.text}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <span className="ins-note" style={{ marginTop: 0 }}>Set by {fx.billingPreference.by}, Jul 30</span>
                  <button type="button" className="ins-btn ins-btn-secondary" style={{ minHeight: 32 }}>Edit</button>
                </div>
              </section>
            </aside>
          </div>
        </div>
      </div>

      {fact ? (
        <FactPopover
          fact={fact}
          onClose={() => { setFact(null); }}
          onWrong={() => { setFact(null); setWrong(true); }}
        />
      ) : null}
      {call ? <CallDrawer call={fx.call} onClose={() => { setCall(false); }} /> : null}
      {portal ? <PortalDrawer portal={fx.portal} onClose={() => { setPortal(false); }} /> : null}
      {wrong ? <WrongForm onClose={() => { setWrong(false); }} /> : null}
      <PrintReport />
    </InsuranceProvider>
  );
}

/** "This is wrong" short form → creates a Worklist item (handoff §4.12, §7). */
function WrongForm({ onClose }: { onClose: () => void }) {
  const [sent, setSent] = useState(false);
  return (
    <div className="ins-pop-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ins-pop" role="dialog" aria-modal="true" aria-label="Report a wrong fact">
        <div className="ins-pop-head">
          <h3 className="ins-drawer-title" style={{ fontSize: 16 }}>{sent ? 'Added to the Worklist' : 'What looks wrong?'}</h3>
          <button type="button" className="ins-drawer-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="ins-pop-body">
          {sent ? (
            <p className="ins-note" style={{ marginTop: 0 }}>ELVA will re-check this fact and a Worklist item has been created for the billing team.</p>
          ) : (
            <>
              <label className="ins-label" htmlFor="ins-wrong">Tell ELVA what’s off</label>
              <textarea id="ins-wrong" rows={3} style={{ width: '100%', marginTop: 6, fontSize: 13, padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', resize: 'vertical' }} />
              <button type="button" className="ins-btn ins-btn-primary" style={{ marginTop: 10 }} onClick={() => { setSent(true); }}>Create Worklist item</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
